/**
 * topicCompliance — the one rule set that says whether a topic language is
 * publishable.
 *
 * A topic page is a content page, so every language has to prove itself:
 * the fields are present, the lengths are inside the search-engine limits,
 * the text is actually written in that language, it is not another topic's
 * text, and the web address follows the topic's own address rule.
 *
 * Pure functions only — no queries, no writes. The editor, the topics table,
 * the audit report and the indexing decision all read from here so they never
 * disagree with each other.
 */

import { FAQ_ITEM_BAND, validateFaqStructure } from '@/config/qualityConfig';

import { fidelityGaps, fidelityMessage, isFaithful, spelledOutInSource } from '@/lib/translationFidelity';

export type TopicComplianceField =

  | 'name'
  | 'slug'
  | 'meta_title'
  | 'meta_description'
  | 'aio_snippet'
  | 'body'
  | 'faq_items';


export type TopicComplianceCode =
  | 'missing'
  | 'too_short'
  | 'too_long'
  | 'wrong_language'
  | 'duplicate'
  | 'address_rule'
  /** The name was translated although the topic's rule says to keep it. */
  | 'name_rule'
  | 'address_not_translated'
  | 'name_not_translated'
  | 'meta_truncated'
  | 'brand_suffix'
  | 'echoed_prompt'
  /** A rate, threshold or deadline with nothing named as setting it. */
  | 'unsourced_figure'
  /** Wording that claims a tax rule holds without exception. */
  | 'absolute_claim'
  /** A figure, link or acronym that changed between source and translation. */
  | 'unfaithful'
  /** A word written in a writing system that is not this language's own. */
  | 'foreign_script'
  | 'malformed';

export interface TopicComplianceIssue {
  field: TopicComplianceField;
  locale: string;
  code: TopicComplianceCode;
  /** Plain-language sentence shown next to the field. */
  message: string;
  /** Character count actually stored, when the rule is about length. */
  actual?: number;
  limit?: number;
}

export const TOPIC_COMPLIANCE_FIELD_LABELS: Record<TopicComplianceField, string> = {
  name: 'Name',
  slug: 'Web address',
  meta_title: 'Page title',
  meta_description: 'Search snippet',
  aio_snippet: 'AI answer',
  body: 'Body',
  faq_items: 'Questions and answers',
};

/**
 * Every rule named in the words an operator reads on the cards. A count of rows
 * "breaking another rule" cannot be acted on; a count per named rule can.
 */
export const TOPIC_COMPLIANCE_CODE_LABELS: Record<TopicComplianceCode, string> = {
  missing: 'field empty',
  too_short: 'text too short',
  too_long: 'text too long',
  duplicate: 'text reused by another topic',
  wrong_language: 'written in the wrong language',
  name_not_translated: 'name still the English wording',
  address_not_translated: 'web address still the English one',
  address_rule: 'web address breaks the keep-name rule',
  name_rule: 'name translated although the rule says keep it',
  brand_suffix: 'page title missing the site name',
  echoed_prompt: 'the instructions were echoed back',
  unsourced_figure: 'a figure with no law named',
  absolute_claim: 'claims a rule holds without exception',
  unfaithful: 'does not match the original',
  foreign_script: 'wrong writing system',
  meta_truncated: 'text ends mid-sentence',
  malformed: 'wrong shape',
};



export interface TopicLengthRule {
  min: number;
  max: number;
  /** Optional fields never fail on absence, only on length. */
  optional?: boolean;
}

/**
 * Character windows, written for a language that reads like English. Every
 * consumer should go through `limitsForLocale` / `limitForLocale` instead, so
 * each writing system is judged by its own numbers.
 */
export const TOPIC_FIELD_LIMITS: Record<TopicComplianceField, TopicLengthRule> = {
  name: { min: 3, max: 60 },
  slug: { min: 3, max: 80 },
  // The page title is built, not written: it is the topic's own name plus the
  // site name. A short official name ("PIS | Tributário") is therefore correct,
  // so the title has no minimum of its own — only the shape check below applies.
  meta_title: { min: 1, max: 60 },
  meta_description: { min: 70, max: 160 },
  aio_snippet: { min: 40, max: 300 },
  // The body is what stops a topic page from being thin, so it has to be a
  // real article: an excerpt-sized paragraph does not qualify.
  body: { min: 1000, max: 40000 },
  // Questions and answers are counted, not measured in characters: the window
  // below is the number of questions the page carries. A page with none stays
  // acceptable, so the field is optional.
  faq_items: { min: FAQ_ITEM_BAND.min, max: FAQ_ITEM_BAND.max, optional: true },
};



// ---------------------------------------------------------------------------
// Per-language length windows
// ---------------------------------------------------------------------------

/**
 * How a language spends characters.
 * - `cjk`: Chinese, Japanese, Korean and Thai say the same thing in far fewer
 *   characters, so English counts would reject correct text as "too short".
 * - `dense`: abugida and consonantal scripts (Devanagari, Bengali, Telugu,
 *   Tamil, Hebrew, Arabic, Greek) write the same sentence in noticeably fewer
 *   characters than English, so the floor comes down while the ceiling stays
 *   generous — a faithful translation must never be rejected for being short.
 * - `wide`: compounding scripts need more room for the same sentence, so only
 *   the ceiling is raised.
 * - `latin`: today's numbers, unchanged.
 */
export type TopicScriptFamily = 'cjk' | 'dense' | 'wide' | 'latin';

const SCRIPT_FAMILY_BY_LOCALE: Record<string, TopicScriptFamily> = {
  zh: 'cjk',
  'zh-TW': 'cjk',
  ja: 'cjk',
  ko: 'cjk',
  th: 'cjk',
  de: 'wide',
  fi: 'wide',
  hu: 'wide',
  ru: 'wide',
  uk: 'wide',
  ta: 'dense',
  te: 'dense',
  hi: 'dense',
  mr: 'dense',
  pa: 'dense',
  gu: 'dense',
  kn: 'dense',
  ml: 'dense',
  bn: 'dense',
  ar: 'dense',
  fa: 'dense',
  ur: 'dense',
  he: 'dense',
  el: 'dense',
};

const FAMILY_FACTORS: Record<TopicScriptFamily, { min: number; max: number }> = {
  cjk: { min: 0.4, max: 0.6 },
  dense: { min: 0.6, max: 1.2 },
  wide: { min: 1, max: 1.2 },
  latin: { min: 1, max: 1 },
};

/**
 * The writing system as `qualityConfig`'s FAQ windows name it.
 *
 * `faqLengthMultiplier` has always supported 'indic' at 0.6, and this call site
 * passed `family === 'cjk' ? 'cjk' : 'latin'` — so the dense scripts it was
 * written for never reached it and were judged at the Latin floor.
 *
 * 'wide' used to fold into 'latin' because the FAQ windows had no factor for it
 * and inventing one here would have been a policy decision in the wrong place.
 * The factor now exists, in qualityConfig beside the others, so the family is
 * passed through and German, Finnish, Hungarian, Russian and Ukrainian get the
 * same 1.2 headroom in a FAQ answer that they already had in every other field.
 */
export function faqScriptType(locale: string): 'cjk' | 'indic' | 'wide' | 'latin' {
  const family = scriptFamilyForLocale(locale);
  if (family === 'cjk') return 'cjk';
  if (family === 'dense') return 'indic';
  if (family === 'wide') return 'wide';
  return 'latin';
}

/** Which writing-system family this language belongs to. */
export function scriptFamilyForLocale(locale: string): TopicScriptFamily {
  return SCRIPT_FAMILY_BY_LOCALE[neutralLocale(locale)] ?? 'latin';
}

/**
 * The web address is never scaled: a slug is a slug in every language. The
 * questions window is a count, not characters, so it is never scaled either.
 */
const UNSCALED_FIELDS: readonly TopicComplianceField[] = ['slug', 'faq_items'];


/**
 * The window one field has to sit inside, in this language.
 *
 * The page title is the topic's own name plus the site name, and the site name
 * is written the same way in every language. Only the name part is scaled: a
 * Korean title judged as if "| Tax.Network" were Korean has no room left for
 * the name itself.
 */
export function limitForLocale(locale: string, field: TopicComplianceField): TopicLengthRule {
  const base = TOPIC_FIELD_LIMITS[field];
  if (UNSCALED_FIELDS.includes(field)) return base;
  const factor = FAMILY_FACTORS[scriptFamilyForLocale(locale)];
  if (factor.min === 1 && factor.max === 1) return base;
  const fixed = field === 'meta_title' ? brandSuffixLength(locale) : 0;
  // A bound already smaller than the site name (the title has no real minimum)
  // stays as it is: adding the fixed part to it would invent a minimum.
  const scale = (value: number, by: number): number =>
    value <= fixed ? value : Math.round((value - fixed) * by) + fixed;
  const min = Math.max(2, scale(base.min, factor.min));
  return {
    ...base,
    min,
    max: Math.max(min + 1, scale(base.max, factor.max)),
  };
}


/** Every window for this language, in one object. */
export function limitsForLocale(locale: string): Record<TopicComplianceField, TopicLengthRule> {
  const fields = Object.keys(TOPIC_FIELD_LIMITS) as TopicComplianceField[];
  return fields.reduce((acc, field) => {
    acc[field] = limitForLocale(locale, field);
    return acc;
  }, {} as Record<TopicComplianceField, TopicLengthRule>);
}

// ---------------------------------------------------------------------------
// What the writer is asked to aim for
// ---------------------------------------------------------------------------

/**
 * Languages that say the same thing in more words than English. Their answers
 * land a handful of characters over the ceiling far more often, so they are
 * asked for a target further inside it.
 */
const VERBOSE_LOCALES: readonly string[] = ['pt', 'es', 'fr', 'it', 'de', 'ro', 'nl', 'pl', 'ru', 'uk', 'el'];

/** How far inside the ceiling a field is asked for, before the language shift. */
const TARGET_HEADROOM: Partial<Record<TopicComplianceField, { near: number; far: number }>> = {
  meta_description: { near: 10, far: 20 },
  aio_snippet: { near: 30, far: 80 },
};

/**
 * The range the writer is *asked* for: comfortably inside the ceiling rather
 * than right at it, and lower still for a language that runs long. Asking for
 * the ceiling is what produced answers a few characters over it.
 */
export function targetForLocale(locale: string, field: TopicComplianceField): TopicLengthRule {
  const limit = limitForLocale(locale, field);
  const headroom = TARGET_HEADROOM[field];
  if (!headroom) return limit;
  const shift = VERBOSE_LOCALES.includes(neutralLocale(locale)) ? Math.round(headroom.near / 2) : 0;
  const max = Math.max(limit.min + 1, limit.max - headroom.near - shift);
  const min = Math.max(limit.min, Math.min(max - 1, limit.max - headroom.far - shift));
  return { ...limit, min, max };
}

// ---------------------------------------------------------------------------
// Brand suffix on the page title
// ---------------------------------------------------------------------------

/** Portuguese pages belong to Tributário, every other language to Tax.Network. */
export const TOPIC_BRAND_SUFFIXES = ['Tributário', 'Tax.Network'] as const;

export function brandSuffixForLocale(locale: string): string {
  const target = (locale ?? '').trim().toLowerCase();
  return target === 'pt' || target.startsWith('pt-') ? 'Tributário' : 'Tax.Network';
}

/** The " | Site" part of a page title: the same length in every language. */
export function brandSuffixLength(locale: string): number {
  return brandSuffixForLocale(locale).length + 3;
}

/** Cuts the title at a word boundary so the brand suffix always fits in 60. */
function fitTitle(title: string, suffix: string, max: number): string {
  const room = max - suffix.length - 3; // " | "
  if (title.length <= room) return title;
  const cut = title.slice(0, Math.max(0, room));
  const boundary = cut.lastIndexOf(' ');
  return (boundary > 12 ? cut.slice(0, boundary) : cut).trim().replace(/[,;:–-]$/, '').trim();
}

/**
 * The page title without any site name on the end: wrong brands and repeats
 * are stripped, whatever the writer or the translator returned.
 */
export function stripBrandSuffix(title: string | null | undefined): string {
  let base = (title ?? '').trim();
  let stripped = true;
  while (stripped) {
    stripped = false;
    for (const brand of TOPIC_BRAND_SUFFIXES) {
      const pattern = new RegExp(`\\s*[|·—–-]\\s*${brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i');
      if (pattern.test(base)) {
        base = base.replace(pattern, '').trim();
        stripped = true;
      }
    }
  }
  return base;
}

/**
 * Exactly one correct brand suffix on the page title, whatever the writer or
 * the translator returned: wrong brands and repeats are stripped first.
 */
export function normalizeBrandTitle(title: string | null | undefined, locale: string): string {
  const suffix = brandSuffixForLocale(locale);
  const base = stripBrandSuffix(title);
  if (!base) return '';

  const max = TOPIC_FIELD_LIMITS.meta_title.max;
  return `${fitTitle(base, suffix, max)} | ${suffix}`;
}


/** Does this title already carry the right brand for its language? */
export function hasCorrectBrandSuffix(title: string | null | undefined, locale: string): boolean {
  const text = (title ?? '').trim();
  if (!text) return true;
  const suffix = brandSuffixForLocale(locale);
  return text.toLowerCase().endsWith(suffix.toLowerCase());
}

/**
 * The page title is a shape, not prose: the topic's own name in this language
 * followed by the site that serves it. Nobody writes it, so nothing can talk to
 * itself in it.
 */
export function brandedTitleFromName(name: string | null | undefined, locale: string): string {
  return normalizeBrandTitle(name, locale);
}


// ---------------------------------------------------------------------------
// Language detection
// ---------------------------------------------------------------------------

/**
 * Locales written in their own script: text in the wrong script is obvious.
 * The Han ranges include the extension blocks and the compatibility block, so a
 * rare character never counts against the language that uses it.
 */
const HAN = '\\u3400-\\u4DBF\\u4E00-\\u9FFF\\uF900-\\uFAFF\\u{20000}-\\u{2A6DF}';

const SCRIPT_BY_LOCALE: Record<string, RegExp> = {
  ar: /[\u0600-\u06FF\u0750-\u077F]/gu,
  fa: /[\u0600-\u06FF\u0750-\u077F]/gu,
  ur: /[\u0600-\u06FF\u0750-\u077F]/gu,
  he: /[\u0590-\u05FF]/gu,
  el: /[\u0370-\u03FF\u1F00-\u1FFF]/gu,
  ru: /[\u0400-\u04FF]/gu,
  uk: /[\u0400-\u04FF]/gu,
  bg: /[\u0400-\u04FF]/gu,
  sr: /[\u0400-\u04FF]/gu,
  hi: /[\u0900-\u097F]/gu,
  mr: /[\u0900-\u097F]/gu,
  ne: /[\u0900-\u097F]/gu,
  pa: /[\u0A00-\u0A7F]/gu,
  gu: /[\u0A80-\u0AFF]/gu,
  bn: /[\u0980-\u09FF]/gu,
  ta: /[\u0B80-\u0BFF]/gu,
  te: /[\u0C00-\u0C7F]/gu,
  kn: /[\u0C80-\u0CFF]/gu,
  ml: /[\u0D00-\u0D7F]/gu,
  si: /[\u0D80-\u0DFF]/gu,
  th: /[\u0E00-\u0E7F]/gu,
  lo: /[\u0E80-\u0EFF]/gu,
  my: /[\u1000-\u109F]/gu,
  km: /[\u1780-\u17FF]/gu,
  ka: /[\u10A0-\u10FF]/gu,
  hy: /[\u0530-\u058F]/gu,
  am: /[\u1200-\u137F]/gu,
  ko: /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/gu,
  ja: new RegExp(`[\\u3040-\\u30FF${HAN}]`, 'gu'),
  zh: new RegExp(`[${HAN}]`, 'gu'),
  'zh-TW': new RegExp(`[${HAN}]`, 'gu'),
};

// ---------------------------------------------------------------------------
// Words written in the wrong writing system
// ---------------------------------------------------------------------------

/**
 * The named writing systems, so a word can be attributed to one of them.
 *
 * Latin letters, digits and punctuation are deliberately absent: they appear in
 * every language we serve (brands, statutes, acronyms, figures) and are never a
 * fault. The Devanagari danda (U+0964/U+0965) is punctuation shared by several
 * Indic languages, so it is excluded from the Devanagari range below.
 */
const NAMED_SCRIPTS: ReadonlyArray<{ key: string; label: string; pattern: RegExp }> = [
  { key: 'devanagari', label: 'Devanagari', pattern: /[\u0900-\u0963\u0966-\u097F]/u },
  { key: 'bengali', label: 'Bangla', pattern: /[\u0980-\u09FF]/u },
  { key: 'gurmukhi', label: 'Gurmukhi', pattern: /[\u0A00-\u0A7F]/u },
  { key: 'gujarati', label: 'Gujarati', pattern: /[\u0A80-\u0AFF]/u },
  { key: 'tamil', label: 'Tamil', pattern: /[\u0B80-\u0BFF]/u },
  { key: 'telugu', label: 'Telugu', pattern: /[\u0C00-\u0C7F]/u },
  { key: 'kannada', label: 'Kannada', pattern: /[\u0C80-\u0CFF]/u },
  { key: 'malayalam', label: 'Malayalam', pattern: /[\u0D00-\u0D7F]/u },
  { key: 'sinhala', label: 'Sinhala', pattern: /[\u0D80-\u0DFF]/u },
  { key: 'thai', label: 'Thai', pattern: /[\u0E00-\u0E7F]/u },
  { key: 'lao', label: 'Lao', pattern: /[\u0E80-\u0EFF]/u },
  { key: 'myanmar', label: 'Myanmar', pattern: /[\u1000-\u109F]/u },
  { key: 'khmer', label: 'Khmer', pattern: /[\u1780-\u17FF]/u },
  { key: 'georgian', label: 'Georgian', pattern: /[\u10A0-\u10FF]/u },
  { key: 'armenian', label: 'Armenian', pattern: /[\u0530-\u058F]/u },
  { key: 'ethiopic', label: 'Ethiopic', pattern: /[\u1200-\u137F]/u },
  { key: 'arabic', label: 'Arabic', pattern: /[\u0600-\u06FF\u0750-\u077F]/u },
  { key: 'hebrew', label: 'Hebrew', pattern: /[\u0590-\u05FF]/u },
  { key: 'greek', label: 'Greek', pattern: /[\u0370-\u03FF\u1F00-\u1FFF]/u },
  { key: 'cyrillic', label: 'Cyrillic', pattern: /[\u0400-\u04FF]/u },
  { key: 'hangul', label: 'Hangul', pattern: /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/u },
  { key: 'kana', label: 'Japanese kana', pattern: /[\u3040-\u30FF]/u },
  { key: 'han', label: 'Han', pattern: new RegExp(`[${HAN}]`, 'u') },
];

/**
 * Which writing systems a language may use. Beyond its own, a language may use
 * the systems its own writing genuinely mixes in: Japanese uses kana and Han,
 * Korean uses Hangul and Han, and Greek mathematics and Latin maxims appear
 * everywhere, so Greek letters are allowed in every language.
 */
const ALLOWED_SCRIPTS_BY_LOCALE: Record<string, readonly string[]> = {
  hi: ['devanagari'], mr: ['devanagari'], ne: ['devanagari'], sa: ['devanagari'],
  bn: ['bengali'], pa: ['gurmukhi'], gu: ['gujarati'], ta: ['tamil'],
  te: ['telugu'], kn: ['kannada'], ml: ['malayalam'], si: ['sinhala'],
  th: ['thai'], lo: ['lao'], my: ['myanmar'], km: ['khmer'],
  ka: ['georgian'], hy: ['armenian'], am: ['ethiopic'],
  ar: ['arabic'], fa: ['arabic'], ur: ['arabic'], he: ['hebrew'],
  el: ['greek'],
  ru: ['cyrillic'], uk: ['cyrillic'], bg: ['cyrillic'], sr: ['cyrillic'], mk: ['cyrillic'],
  ko: ['hangul', 'han'],
  ja: ['kana', 'han'],
  zh: ['han'], 'zh-TW': ['han'],
};

/** Greek letters are used as symbols in every language, so never a fault. */
const UNIVERSAL_SCRIPTS: readonly string[] = ['greek'];

/** A run of letters long enough that it is a word, not a stray symbol. */
const FOREIGN_RUN_MIN = 2;

/**
 * The first word in this text written in a writing system this language does
 * not use, with the name of that system — or null when the text is clean.
 *
 * This catches the translator slipping a word of another language's script into
 * an otherwise correct translation, which no length or language check sees:
 * a Gujarati word inside Chinese leaves both the length and the Chinese-ness of
 * the text intact.
 */
export function findForeignScript(
  value: string | null | undefined,
  locale: string,
  keep: readonly string[] = [],
): { word: string; script: string } | null {
  const text = stripLanguageNeutral(String(value ?? ''), keep);
  if (!text) return null;
  const target = neutralLocale(locale);
  const allowed = new Set<string>([
    ...(ALLOWED_SCRIPTS_BY_LOCALE[locale] ?? ALLOWED_SCRIPTS_BY_LOCALE[target] ?? []),
    ...UNIVERSAL_SCRIPTS,
  ]);

  // One pass over the whole text first. Almost every version is clean, and
  // word-by-word testing against every writing system is what made a full audit
  // of thousands of rows too slow to finish. Only text that already contains a
  // disallowed letter is worth naming the word for.
  const screen = foreignScreenFor(locale, allowed);
  if (!screen.test(text)) return null;

  for (const word of text.split(/[\s\p{P}\p{S}]+/u)) {
    if (word.length < FOREIGN_RUN_MIN) continue;
    if (!screen.test(word)) continue;
    for (const script of NAMED_SCRIPTS) {
      if (allowed.has(script.key)) continue;
      if (!script.pattern.test(word)) continue;
      // Only a run of that script is a word in it; one borrowed sign is not.
      const run = Array.from(word).filter(char => script.pattern.test(char)).length;
      if (run < FOREIGN_RUN_MIN) continue;
      return { word, script: script.label };
    }
  }
  return null;
}

/** One regex covering every writing system this language may not use, built once. */
const FOREIGN_SCREENS = new Map<string, RegExp>();

function foreignScreenFor(locale: string, allowed: ReadonlySet<string>): RegExp {
  const cached = FOREIGN_SCREENS.get(locale);
  if (cached) return cached;
  const ranges = NAMED_SCRIPTS.filter(script => !allowed.has(script.key))
    .map(script => script.pattern.source.replace(/^\[|\]$/g, ''))
    .join('');
  const screen = new RegExp(`[${ranges}]`, 'u');
  FOREIGN_SCREENS.set(locale, screen);
  return screen;
}


/** Words that give a language away, for the Latin-script languages we serve. */
const MARKERS: Record<string, string[]> = {
  en: ['the', 'and', 'of', 'for', 'with', 'that', 'this', 'are', 'is', 'to', 'in', 'on'],
  pt: ['de', 'da', 'do', 'que', 'para', 'com', 'uma', 'não', 'os', 'as', 'sobre', 'é'],
  es: ['de', 'la', 'el', 'que', 'para', 'con', 'una', 'los', 'las', 'sobre', 'es', 'del'],
  fr: ['de', 'la', 'le', 'les', 'des', 'pour', 'avec', 'une', 'sur', 'est', 'que', 'du'],
  it: ['di', 'la', 'il', 'che', 'per', 'con', 'una', 'sono', 'sul', 'del', 'gli', 'è'],
  de: ['der', 'die', 'das', 'und', 'für', 'mit', 'ist', 'von', 'den', 'nicht', 'eine', 'auf'],
  nl: ['de', 'het', 'een', 'en', 'van', 'voor', 'met', 'is', 'niet', 'dat', 'op', 'die'],
  pl: ['i', 'w', 'na', 'nie', 'do', 'jest', 'że', 'dla', 'z', 'oraz'],
  tr: ['ve', 'bir', 'için', 'ile', 'bu', 'olarak', 'daha', 'olan'],
  id: ['dan', 'yang', 'untuk', 'dengan', 'dari', 'pada', 'adalah', 'ini'],
  ms: ['dan', 'yang', 'untuk', 'dengan', 'dari', 'pada', 'adalah', 'ini'],
  vi: ['và', 'của', 'cho', 'với', 'là', 'các', 'được', 'trong'],
  sv: ['och', 'för', 'med', 'är', 'att', 'den', 'som', 'inte'],
  da: ['og', 'for', 'med', 'er', 'at', 'den', 'som', 'ikke'],
  nb: ['og', 'for', 'med', 'er', 'som', 'ikke', 'den', 'til'],
  fi: ['ja', 'on', 'ei', 'että', 'sekä', 'tai', 'kun'],
  cs: ['a', 'je', 'na', 'pro', 'se', 'že', 'nebo'],
  ro: ['și', 'de', 'pentru', 'este', 'cu', 'care', 'din'],
};

function neutralLocale(locale: string): string {
  const raw = (locale ?? '').trim();
  if (!raw) return 'en';
  if (raw.toLowerCase() === 'zh-tw') return 'zh-TW';
  return raw.split('-')[0].toLowerCase();
}

function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/<[^>]*>/g, ' ')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

function markerScore(list: string[] | undefined, tokens: string[]): number {
  if (!list) return 0;
  const seen = new Set(tokens);
  return list.filter(word => seen.has(word)).length;
}

/** Text that belongs to no language: brand, acronyms, figures, addresses. */
const ACRONYM = /\b[A-Z][A-Z0-9./-]{1,7}\b/g;
const URL_LIKE = /\b(?:https?:\/\/|www\.)\S+|\b[\w-]+\.(?:com|net|org|br|network)\b/gi;

/**
 * Strips everything a translation legitimately keeps in its original form, so
 * it never counts against the language: the brand suffix, official acronyms
 * ("IRPJ", "CNPJ", "OECD"), figures and web addresses — plus any wording the
 * topic's own rule says to keep (a statutory name, the original address).
 */
function stripLanguageNeutral(text: string, keep: readonly string[] = []): string {
  let clean = text.replace(/<[^>]*>/g, ' ');
  for (const brand of TOPIC_BRAND_SUFFIXES) {
    clean = clean.split(brand).join(' ');
  }
  for (const phrase of keep) {
    const value = (phrase ?? '').trim();
    if (value.length < 3) continue;
    clean = clean.split(value).join(' ');
  }
  return clean
    .replace(URL_LIKE, ' ')
    .replace(ACRONYM, ' ')
    .replace(/[\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface LanguageCheckOptions {
  /** Wording kept in its original form on purpose, excluded from the verdict. */
  keep?: readonly string[];
}

/**
 * Is this text written in the language it is stored under?
 *
 * Returns null when we cannot tell — a short label, or a language we have no
 * markers for. Only a confident "no" ever raises an issue.
 */
export function looksLikeLocale(
  text: string,
  locale: string,
  options: LanguageCheckOptions = {},
): boolean | null {
  const clean = stripLanguageNeutral(text, options.keep);
  // How much text is needed before a verdict is honest. Dense scripts say more
  // in fewer characters, so a correct short Chinese title is judgeable where an
  // English fragment of the same length is not.
  const minimumToJudge = scriptFamilyForLocale(locale) === 'cjk' ? 10 : 25;
  if (graphemeCount(clean) < minimumToJudge) return null;
  const target = neutralLocale(locale);

  const script = SCRIPT_BY_LOCALE[target];
  if (script) {
    const matches = clean.match(script)?.length ?? 0;
    const letters = clean.match(/\p{L}/gu)?.length ?? 0;
    if (letters === 0) return null;
    return matches / letters >= 0.2;
  }


  if (target === 'en') return null;
  const list = MARKERS[target];
  if (!list) return null;
  const tokens = words(clean);
  if (tokens.length < 8) return null;
  const own = markerScore(list, tokens);
  const english = markerScore(MARKERS.en, tokens);
  // Only a clear English lead counts as wrong: many languages share short words.
  if (english >= 3 && english > own + 1) return false;
  return null;
}

/**
 * Latin maxims tax law uses untranslated in every language ("in dubio pro reo",
 * "non bis in idem"): their English-looking function words never count against
 * the language they appear in.
 */
const LATIN_MAXIM_WORDS = new Set([
  'dubio', 'pro', 'reo', 'bis', 'idem', 'non', 'lex', 'ius', 'jus', 'iuris',
  'ne', 'ultra', 'petita', 'nulla', 'poena', 'sine', 'lege', 'onus', 'probandi',
  'ratio', 'legis', 'erga', 'omnes', 'inter', 'partes', 'ex', 'officio',
  'tributi', 'solutio', 'indebiti',
]);

/**
 * Is a topic's own name written in a language other than the one the topic is
 * filed under? Only a confident mismatch counts:
 *
 *  - a language written in its own script (Chinese, Arabic, Hindi, Greek …)
 *    whose name carries none of that script at all;
 *  - a Latin-script language whose name leans on English function words
 *    ("Statute of Limitations", "Technology in Tax Services") and none of its
 *    own.
 *
 * Acronyms and official short forms ("ICMS", "AFRMM"), Latin maxims, and topics
 * whose rule is to keep their name everywhere are never flagged.
 */
export function sourceNameLanguageMismatch(
  name: string | null | undefined,
  locale: string,
  translationRule?: string | null,
): boolean {
  if (translationRule === 'keep_name') return false;
  const raw = (name ?? '').trim();
  if (!raw) return false;
  const target = neutralLocale(locale);
  if (target === 'en') return false;

  const clean = stripLanguageNeutral(raw);
  const letters = clean.match(/\p{L}/gu)?.length ?? 0;
  if (letters < 4) return false;

  const script = SCRIPT_BY_LOCALE[target];
  if (script) {
    const matches = clean.match(script)?.length ?? 0;
    return matches === 0;
  }

  const list = MARKERS[target];
  if (!list) return false;
  const tokens = words(clean);
  if (tokens.length < 2) return false;
  if (tokens.some(token => LATIN_MAXIM_WORDS.has(token))) return false;
  const own = markerScore(list, tokens);
  const english = markerScore(MARKERS.en, tokens);
  return english >= 1 && own === 0;
}

// ---------------------------------------------------------------------------
// Terms a language borrows from English as its own
// ---------------------------------------------------------------------------

/**
 * Terms that are the accepted wording *in that language*, identical to English.
 * German, Dutch, Danish and Italian tax practice says "Corporate Governance",
 * "Transfer Pricing" and "Compliance" — translating them would be wrong, so a
 * name and an address matching one of these is not an untranslated name.
 *
 * This is maintained as data, one entry per language, never inferred per row: a
 * rule that guesses which English words a language keeps would clear real gaps
 * along with the false ones.
 */
/**
 * Terms international tax practice uses in English whatever the language.
 *
 * This was seven locales each carrying an identical four-item list, which is a
 * table that cannot be extended without editing it seven times — and it was
 * short. Measured 2026-09-21, 15 rows were reported as "name still the English
 * wording" or "web address still the English one" and every one of them is the
 * term that language actually uses:
 *
 *   Transfer Pricing      da, id      Corporate Governance   da, de, nl
 *   Partnership           cs, tl      Anti-Money Laundering  nl, tl
 *   Withholding Tax       tl          Holding Company        tl
 *   Private Client        da, nb      Estate Planning        nl
 *   Exit Tax              it
 *
 * One list, and the locales that accept it, so adding a term is one edit. The
 * list is what constrains this, not the locale set: only terms that are genuinely
 * the local usage belong on it, and a term nobody borrows stays off it.
 */
const BORROWED_TAX_TERMS: readonly string[] = [
  'corporate governance',
  'transfer pricing',
  'compliance',
  'due diligence',
  'partnership',
  'holding company',
  'private client',
  'estate planning',
  'exit tax',
  'withholding tax',
  'anti-money laundering',
  'anti-money laundering (aml)',
  'anti money laundering',
];

/**
 * Languages that write these terms in English.
 *
 * The seven this list already had, plus the three the corpus showed borrowing —
 * Czech ("Partnership"), Tagalog ("Withholding Tax", "Holding Company") and
 * Indonesian ("Transfer Pricing").
 *
 * Deliberately not the Romance source languages. Portuguese has "Governança
 * Corporativa" and Spanish has "Gobierno Corporativo"; borrowing there is a
 * failure to translate, and `topicComplianceSot.test.ts` has asserted exactly
 * that since this list was written. Italian is here because it was, and because
 * "exit tax" really is the Italian usage.
 *
 * A language written in its own script is absent by design: it renders the term
 * in that script, so English wording there is a genuine failure to translate and
 * must keep being reported.
 */
const BORROWING_LOCALES: ReadonlySet<string> = new Set([
  'da', 'de', 'nl', 'it', 'sv', 'nb', 'fi', 'cs', 'tl', 'id',
]);

/** Is this wording the accepted term in this language, even though it is English? */
export function isAcceptedBorrowedTerm(
  value: string | null | undefined,
  locale: string,
): boolean {
  const raw = (value ?? '').trim().toLowerCase();
  if (!raw) return false;
  if (!BORROWING_LOCALES.has(neutralLocale(locale))) return false;
  // An address is the same term written for a URL: "corporate-governance".
  const spaced = raw.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  // A trailing acronym is part of the term, however it is written. The name
  // carries it in brackets — "Anti-Money Laundering (AML)" — and the address
  // carries it as another word, "anti-money-laundering-aml", because brackets do
  // not survive a URL. Only the first was handled, so the name was exempt and
  // its own address was still reported. Two rows on 2026-09-21, and
  // `isAcronymSlug` does not cover them: it only matches an address that is
  // nothing but an acronym, up to eight characters.
  const withoutAcronym = spaced
    .replace(/\s*\([a-z0-9]{2,6}\)\s*$/, '')
    .replace(/\s+[a-z0-9]{2,6}$/, '')
    .trim();
  return BORROWED_TAX_TERMS.includes(raw)
    || BORROWED_TAX_TERMS.includes(spaced)
    || BORROWED_TAX_TERMS.includes(withoutAcronym);
}





// ---------------------------------------------------------------------------
// Answers that are the request instead of the answer
// ---------------------------------------------------------------------------

/**
 * Words that only appear when a writer answered with the request it was given
 * ("Translate the following Portuguese topic name into Simplified Chinese",
 * "Bengali translation of the topic title") instead of the wording asked for.
 * A tax topic never talks about translating itself, so this is safe to reject.
 */
/**
 * A word that means "translate" and nothing else.
 *
 * Three stems were removed from this list because in the languages they belong
 * to they are ordinary tax vocabulary, and scanning for them as substrings
 * refused 65 correct translations — every `echoed_prompt` row in the corpus on
 * 2026-09-21, without a single true positive among them:
 *
 * - `dịch` is Vietnamese for translate, and also for *service* and
 *   *transaction*. `Thuế Dịch vụ` is the Service Tax (ISS) and `Thuế Giao dịch
 *   Tài chính` is the Financial Transaction Tax (IOF). 115 fields across 41
 *   topics — effectively the whole Vietnamese locale on any services topic.
 * - `แปล` is Thai for translate, and sits inside `การแปลง`, *conversion*. Every
 *   Thai topic about digitalising a process carried it. 12 fields. Thai has no
 *   word boundaries, so no amount of anchoring inside the word helps.
 * - `перевод` is Russian for translation, and also for a *remittance*.
 *   CIDE-Remessas is a tax on foreign remittances. 4 fields.
 *
 * They are not recoverable as substrings, so they are gone. What replaces them
 * is the anchored heading forms below: an echo announces itself at the start of
 * the field ("Bản dịch:", "คำแปล:", "Перевод на русский:"), and a field that
 * merely discusses a remittance does not. Those forms match nothing in the
 * 37,595 fields of the current corpus.
 *
 * A Vietnamese, Thai or Russian echo that avoids the heading forms is still
 * caught by the English markers here — the prompt is written in English, so an
 * echo of it usually is too — and by the persona, refusal and wrong-language
 * checks.
 */
const INSTRUCTION_MARKERS: readonly RegExp[] = [
  /\btranslat(?:e|es|ed|ing|ion|ions)\b/i,
  /tradu(?:c|z|ç|ción|ction|zione|ções)/i,
  /(?:übersetz|vertaal|översätt|oversæt|käännö|käänn|přelož|překlad|tłumacz|traducere|çevir)/i,
  /(?:翻译|翻譯|翻訳|번역|अनुवाद|অনুবাদ|மொழிபெயர்|అనువాద|ترجم|תרגום)/,
  /(?:terjemah|penterjemahan|penerjemahan|pagsasalin|isalin)/i,
  /\b(?:reply with|the following|as follows|source text|target language|only acceptable language)\b/i,
];

/**
 * The writer's own persona line, handed back as if it were the text ("As a
 * senior tax professional content writer…", and the same sentence in every
 * language the pipeline writes). A topic never describes its own author, so any
 * field opening this way is the request, not the answer.
 */
const PERSONA_MARKERS: readonly RegExp[] = [
  /^(?:as|being)\s+(?:a|an)\s+[^.]{0,60}\b(?:writer|professional|expert|specialist|assistant|translator)\b/i,
  /^(?:som|as|come|como|comme|als|kao|sebagai|bilang|jako|ca|ako)\s+(?:en|et|ein|eine|un|una|uno|uma|a|an|isang|seorang|penulis)?\s*[^.]{0,60}\b(?:indholdsforfatter|innholdsforfatter|innehållsförfattare|sisällöntuottaja|contentschrijver|redattore|redator|redactor|escritor|escritora|écrivain|rédacteur|autor|autore|schreiber|verfasser|penulis|manunulat|yazar|писатель|автор)\b/i,
  /^(?:sebagai|bilang)\s+[^.]{0,60}\b(?:senior|profesional|profesyonel|professional)\b/i,
  /\b(?:senior tax professional content|tax professional content writer)\b/i,
];

/**
 * The writer declining the request. Never text, in any language.
 */
const REFUSAL_MARKERS: readonly RegExp[] = [
  /^(?:i\s+(?:cannot|can't|am unable|won't)|sorry,?\s+(?:i|but))/i,
  /^(?:maaf|mohon maaf|lo siento|desculpe|désolé|es tut mir leid|üzgünüm|beklager|ledsen|pahoittelen|sajnálom|извините|抱歉|申し訳|죄송)/i,
  /\b(?:tidak dapat memenuhi|cannot fulfil|cannot fulfill|can not fulfill|as an ai (?:language )?model)\b/i,
];

/**
 * An instruction heading saved in place of a title ("Page title translation
 * guidelines", "Instruksi Penerjemahan Judul Halaman").
 */
const INSTRUCTION_HEADING_MARKERS: readonly RegExp[] = [
  /^(?:instruction|instructions|guideline|guidelines|prompt|request|task)\b/i,
  // `panduan`, `pedoman` and `arahan` are gone: they are the ordinary Indonesian
  // and Malay nouns for a guide, and a description that opens "Panduan IPVA atas
  // pesawat dan kapal" is a guide to the tax, not an instruction handed back.
  // Two rows on 2026-09-21, and the whole locale at risk on any topic a writer
  // would naturally describe as a guide.
  //
  // `instruções` is gone for the same reason and a sharper one: Instrução
  // Normativa is a Brazilian tax instrument, so a Portuguese topic naming one
  // opens with exactly this word.
  //
  // What remains are headings that only a machine writes: "Instructions:",
  // "Instruksi Penerjemahan", "Anweisung".
  /^(?:instruks|instruksi|instruksjoner|instrucciones|istruzioni|instrucțiuni|riktlinjer|retningslinjer|richtlijnen|anweisung|consignes|указания|инструкц)\b/i,
  /^(?:harap|mohon|sila|please|veuillez|si prega|por favor|bitte|vennligst|venligst|var vänlig|prosím|lütfen|пожалуйста)\b/i,
  /^(?:berikut adalah|here is the|the provided content|this instruction|the content provided|deze instructie|conform instrucțiunilor|conținutul furnizat|de verstrekte inhoud|untuk terjemahan|dokumen ini|pagsasaalang-alang)/i,
  // The three languages whose word for "translate" had to leave
  // INSTRUCTION_MARKERS, caught here instead. Anchored, so `dịch` inside
  // "Thuế Dịch vụ", `แปล` inside "การแปลง" and `перевод` meaning a remittance
  // are all out of reach, while a field that opens by announcing itself as a
  // translation is not.
  /^(?:bản dịch|dịch sang|dịch thuật)\b/i,
  /^(?:คำแปล|แปลเป็น|การแปลข)/,
  /^(?:перевод на|перевод текста|перевод:|переклад на|переклад:)/i,
];

/** How much of a long text can carry the request before it counts as echoed. */
const INSTRUCTION_WINDOW = 240;

/**
 * Did the writer hand back the request, its own persona, or a refusal instead of
 * the text? Such an answer must never be stored: it is neither the original nor
 * a translation of it.
 */
export function isEchoedInstruction(value: string | null | undefined): boolean {
  const text = (value ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return false;
  const window = text.slice(0, INSTRUCTION_WINDOW);
  if (INSTRUCTION_MARKERS.some(pattern => pattern.test(window))) return true;
  if (PERSONA_MARKERS.some(pattern => pattern.test(window))) return true;
  if (REFUSAL_MARKERS.some(pattern => pattern.test(window))) return true;
  return INSTRUCTION_HEADING_MARKERS.some(pattern => pattern.test(window));
}

/** The longest a topic name can be before it is prose rather than a term. */
const NAME_TERM_MAX = 80;

/**
 * A name that is a sentence or a paragraph rather than a term: too long to be a
 * term, or carrying sentence punctuation and several words after it.
 */
export function isProseName(value: string | null | undefined): boolean {
  const text = (value ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return false;
  if (text.length > NAME_TERM_MAX) return true;
  // A full stop, question or exclamation mark followed by more words: prose.
  return /[.!?。！？]\s+\p{L}/u.test(text);
}

// ---------------------------------------------------------------------------
// The check
// ---------------------------------------------------------------------------

export interface TopicComplianceInput {
  locale: string;
  /** The topic's own language: shared fields are only judged there. */
  isSourceLocale: boolean;
  name: string | null;
  slug: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  aioSnippet: string | null;
  body?: string | null;
  /**
   * How long this language's page text is, when the text itself is not carried
   * here. The reports use it so a thin page counts the same on every screen
   * without shipping every article. Ignored when `body` is given.
   */
  bodyLength?: number;
  /**
   * The page's questions and answers, when they are loaded here. `undefined`
   * means "not read here"; an empty list is a page with none, which is allowed.
   */
  faqItems?: Array<{ question: string; answer: string }> | null;

  /** 'keep_name' topics reuse the source address in every language. */
  translationRule?: string | null;
  sourceSlug?: string | null;
  /** The name in the topic's own language, to catch a name never translated. */
  sourceName?: string | null;
  /**
   * The same fields in the topic's own language. When they are given, the
   * translation is compared against them: a figure, link or acronym that
   * changed between the two is a faithfulness fault, not a length problem.
   */
  sourceMetaTitle?: string | null;
  sourceMetaDescription?: string | null;
  sourceAioSnippet?: string | null;
  sourceBody?: string | null;
  /**
   * The original page's own length, used only to keep this language's minimum
   * honest. Page text written fresh for a language is not a translation of the
   * original page, so it is never judged for figure-by-figure fidelity.
   */
  sourceBodyForLength?: string | null;
  /** The original page's length, when its text is not carried here. */
  sourceBodyLength?: number;

  /** Other topics already using the same text in this language, per field. */
  duplicates?: Partial<Record<TopicComplianceField, string[]>>;
}

/** The grapheme segmenter, described locally so no newer library is required. */
interface GraphemeSegmenter {
  segment(input: string): Iterable<{ segment: string }>;
}
type SegmenterConstructor = new (
  locales?: string,
  options?: { granularity: 'grapheme' },
) => GraphemeSegmenter;

const SEGMENTER: GraphemeSegmenter | null = (() => {
  const ctor = (Intl as unknown as { Segmenter?: SegmenterConstructor }).Segmenter;
  if (typeof ctor !== 'function') return null;
  try {
    return new ctor(undefined, { granularity: 'grapheme' });
  } catch {
    return null;
  }
})();

/**
 * Characters as a reader perceives them: an accented letter, a Devanagari or
 * Bangla syllable with its vowel marks, an Arabic letter with its diacritics and
 * an emoji each count once, whatever their storage size.
 */
function graphemeCount(text: string): number {
  if (!text) return 0;
  if (SEGMENTER) {
    let count = 0;
    for (const _segment of SEGMENTER.segment(text)) count += 1;
    return count;
  }
  return Array.from(text).length;
}

/**
 * How much text a reader actually gets: markup removed and runs of spaces
 * collapsed, so a page full of tags cannot pass as a page full of words.
 */
export function visibleTextLength(value: string | null | undefined): number {
  const text = (value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return graphemeCount(text);
}

function textLength(value: string | null | undefined): number {
  return visibleTextLength(value);
}

/**
 * The floor one field is actually held to in this language.
 *
 * A translation can never be longer than what the original says: asking for
 * more characters than the source carries is asking the writer to invent text.
 * So when the source text is known, its own length (allowing for a language
 * that says the same thing in fewer characters) caps the requirement.
 */
export function minimumFor(
  locale: string,
  field: TopicComplianceField,
  sourceText?: string | null,
  /** The source's own length, for a screen that knows it without the text. */
  knownSourceLength?: number,
): number {
  const rule = limitForLocale(locale, field);
  const sourceLength = knownSourceLength ?? textLength(sourceText);
  if (sourceLength === 0) return rule.min;
  return Math.max(1, Math.min(rule.min, Math.round(sourceLength * 0.6)));
}

function checkField(
  field: TopicComplianceField,
  value: string | null | undefined,
  locale: string,
  issues: TopicComplianceIssue[],
  sourceText?: string | null,
): void {
  // The page title carries a fixed " | Site" the platform adds itself. That part
  // is the same length in every language, so it is not spent from the language's
  // own character window — only the name part is judged.
  const rawLength = textLength(value);
  const suffixAllowance =
    rawLength > 0 && field === 'meta_title' && hasCorrectBrandSuffix(value, locale)
      ? brandSuffixLength(locale)
      : 0;
  checkFieldLength(field, Math.max(0, rawLength - suffixAllowance), locale, issues, sourceText);
}

/** The same length rules, for a screen that knows the length but not the text. */
function checkFieldLength(
  field: TopicComplianceField,
  length: number,
  locale: string,
  issues: TopicComplianceIssue[],
  sourceText?: string | null,
  knownSourceLength?: number,
): void {
  const rule = limitForLocale(locale, field);
  const label = TOPIC_COMPLIANCE_FIELD_LABELS[field];
  const minimum = minimumFor(locale, field, sourceText, knownSourceLength);

  if (length === 0) {
    if (!rule.optional) {
      issues.push({ field, locale, code: 'missing', message: `${label} is empty.` });
    }
    return;
  }
  if (length < minimum) {
    issues.push({
      field,
      locale,
      code: 'too_short',
      actual: length,
      limit: minimum,
      message: `${label} is too short — ${length} characters, at least ${minimum} needed.`,
    });
    return;
  }
  if (length > rule.max) {
    issues.push({
      field,
      locale,
      code: 'too_long',
      actual: length,
      limit: rule.max,
      message: `${label} is too long — ${length} characters, keep it under ${rule.max}.`,
    });
  }
}


/**
 * Questions and answers. A page with none is allowed; as soon as it has one it
 * has to carry a real set: the shared band, and every question and answer inside
 * its own window. The wording of each reason comes from the shared rules so the
 * editors, the reports and the writers all say the same thing.
 */
function checkFaqField(
  items: Array<{ question: string; answer: string }> | null | undefined,
  locale: string,
  issues: TopicComplianceIssue[],
): void {
  const label = TOPIC_COMPLIANCE_FIELD_LABELS.faq_items;
  const list = (items ?? []).filter(item => item.question.trim() || item.answer.trim());
  if (list.length === 0) return;

  if (list.length < FAQ_ITEM_BAND.min) {
    issues.push({
      field: 'faq_items',
      locale,
      code: 'too_short',
      actual: list.length,
      limit: FAQ_ITEM_BAND.min,
      message: `${label}: only ${list.length} question${list.length === 1 ? '' : 's'} — at least ${FAQ_ITEM_BAND.min} are needed (aim for ${FAQ_ITEM_BAND.target}).`,
    });
    return;
  }
  if (list.length > FAQ_ITEM_BAND.max) {
    issues.push({
      field: 'faq_items',
      locale,
      code: 'too_long',
      actual: list.length,
      limit: FAQ_ITEM_BAND.max,
      message: `${label}: ${list.length} questions — keep it to ${FAQ_ITEM_BAND.max} at most.`,
    });
    return;
  }

  const reason = validateFaqStructure(list, faqScriptType(locale), locale);
  if (reason) {
    issues.push({
      field: 'faq_items',
      locale,
      code: 'malformed',
      message: `${label}: ${reason}.`,
    });
  }
}


/**
 * Words that name what sets a figure. A sentence carrying a rate, an amount or a
 * deadline has to carry one of these too, in any of the platform's languages —
 * otherwise the reader has a number they cannot check and we cannot defend.
 */
const INSTRUMENT_WORDS: readonly string[] = [
  'irc', 'usc', 'cfr', 'section', 'sec.', '§', 'art.', 'article', 'artigo', 'artículo',
  'lei', 'ley', 'loi', 'legge', 'gesetz', 'wet', 'lov', 'lag', 'ustawa', 'zakon',
  'decreto', 'décret', 'decret', 'dekret', 'decree', 'regulation', 'regulamento',
  'reglamento', 'règlement', 'verordnung', 'directive', 'diretiva', 'directiva',
  'instrução normativa', 'instrucao normativa', 'ato normativo', 'portaria',
  'resolução', 'resolucao', 'resolución', 'resolution', 'código', 'codigo', 'code',
  'kodeks', 'treaty', 'tratado', 'convention', 'convenção', 'convencao', 'act',
  'statute', 'lei complementar', 'emenda', 'constitution', 'constituição',
  // Precisely named international rule frameworks also provide an attributable
  // source. Keep this specific: the generic word "rules" is not sufficient.
  'global anti-base erosion',
  // The list above is Latin-script and covers about fifteen languages. We
  // publish in thirty-six, and a translation that names its instrument in its
  // own language was read as naming none: measured 2026-09-21, ICMS reported a
  // rate with no law named in Korean, Chinese, Arabic, Hungarian and Czech, and
  // every one of those rows says "Senate resolution" — 연방 상원 결의안,
  // 联邦参议院决议, قرار مجلس الشيوخ, szenátusi határozat, usnesení senátu.
  //
  // The word for a law is a small closed set per language, so it is written out
  // rather than guessed at from the script.
  'határozat', 'törvény', 'rendelet', 'usnesení', 'nařízení', 'vyhláška',
  'uchwała', 'rozporządzenie', 'hotărâre', 'ordonanță',
  'karar', 'kanun', 'yönetmelik', 'tebliğ',
  'keputusan', 'peraturan', 'undang-undang', 'ketetapan',
  'kautusan', 'batas', 'kapasiyahan',
  'nghị quyết', 'luật', 'nghị định', 'thông tư',
  'постанова', 'постановление', 'закон', 'указ', 'приказ',
  'νόμος', 'νόμο', 'απόφαση', 'διάταγμα',
  'חוק', 'תקנה', 'צו',
  'قانون', 'مرسوم', 'لائحة', 'قرار',
  'कानून', 'अधिनियम', 'अधिसूचना', 'संकल्प',
  'আইন', 'অধ্যাদেশ', 'প্রজ্ঞাপন',
  'ਕਾਨੂੰਨ', 'ਐਕਟ',
  'చట్టం', 'ఉత్తర్వు', 'తీర్మానం',
  'กฎหมาย', 'พระราชบัญญัติ', 'ประกาศ', 'มติ',
  '法', '法律', '条例', '決議', '决议', '規則', '规则', '通達',
  '법', '법률', '시행령', '결의안', '고시',
  'lag', 'lov', 'forskrift', 'bekendtgørelse', 'föreskrift',
  'laki', 'asetus', 'päätös',
  'besluit', 'verordening', 'richtlijn',
];

/**
 * Blocks, so a figure is judged with the text around it. A paragraph that
 * introduces a list belongs with that list: the instrument is usually named
 * once, in the introduction, and the figures follow in the items. Headings and
 * the end of a list close a block; nothing smaller does.
 */
function blocksOf(value: string): string[] {
  return (value ?? '')
    .replace(/&nbsp;/gi, ' ')
    .split(/<\/?h[1-6][^>]*>|<\/(?:ul|ol|table|blockquote)>|\n\s*\n/giu)
    .map(part => part.replace(/<[^>]*>/g, ' ').replace(/\s+/gu, ' ').trim())
    .filter(Boolean);
}

/** A figure strong enough to need a source: a percentage or a monetary amount. */
const FIGURE_PATTERN =
  /(\d{1,3}(?:[.,]\d+)?\s?%)|((?:R\$|US\$|\$|€|£|¥|₹|BRL|USD|EUR|GBP)\s?\d)|(\d[\d.,]*\s?(?:%|percent|por cento|prozent|pour cent))/iu;

/**
 * A figure that is part of a rule's proper name — the "200% Rule", the
 * "183-day rule", the "three-property rule". Naming a rule is not stating a
 * rate, so these are removed before a block is judged.
 */
const NAMED_RULE_PATTERNS: readonly RegExp[] = [
  /\d[\d.,]*\s?%\s*[-–]?\s*(rule|test|threshold|regra|teste|regel|règle|regola|regla)/giu,
  /\d[\d.,]*\s?[-–]\s?(day|month|year|property|dia|dias|mes|meses|tag|jour|giorno)\s*[-–]?\s*(rule|test|regra|teste|regel|règle|regola|regla)/giu,
];

/** The block with rule names taken out, so only real claims remain. */
function withoutNamedRules(block: string): string {
  return NAMED_RULE_PATTERNS.reduce((text, pattern) => text.replace(pattern, ' '), block);
}

/**
 * Figures stated with nothing named as setting them. Returns the offending
 * text, shortened, so the editor can quote the first one back.
 *
 * A page is read in order: once a law, article, decree or regulation has been
 * named — in the figure's own block, in the heading above it, or anywhere
 * earlier on the page — the figures that follow are covered by it. A page that
 * names no instrument at all is still reported on its figures, which is the case
 * this rule exists for.
 */
export function findUnsourcedFigures(
  value: string | null | undefined,
  sourceValue?: string | null,
): string[] {
  const found: string[] = [];
  let instrumentSeen = false;
  // A translation is judged only on the figures it introduces.
  //
  // `INSTRUMENT_WORDS` is Latin-script. A correct Arabic, Japanese or Greek
  // translation that names its law in its own script contains none of those
  // words, so `instrumentSeen` stayed false and every rate it carried was
  // reported. Measured on 2026-09-20: of the rows stating a rate, 33.7% of
  // non-Latin rows carried no Latin instrument word against 6.4% of Latin ones,
  // and the count held at exactly 300 through a full rewrite of 2,016 rows —
  // because it was a property of the writing system, not of the text.
  //
  // Worse, it could not be fixed by writing better. The contract asked for the
  // instrument to be named; the model answered by inventing a citation, which
  // left the rule still failing and added a figure the source never had, so the
  // row then failed `unfaithful` as well.
  //
  // So when a source is supplied the question becomes the one the rule is
  // actually for: did this translation introduce a figure the source never
  // stated? A figure the source carries was already judged in the source
  // language, where the instrument is named in a script this list covers, and
  // the text to fix is that source row — once, for all languages.
  const sourceFigures = sourceValue === undefined || sourceValue === null
    ? null
    : figuresIn(blocksOf(sourceValue).join(' '));
  for (const block of blocksOf(value ?? '')) {
    const lower = block.toLowerCase();
    if (INSTRUMENT_WORDS.some(word => lower.includes(word))) instrumentSeen = true;
    if (instrumentSeen) continue;
    const judged = withoutNamedRules(block);
    if (!FIGURE_PATTERN.test(judged)) continue;
    if (sourceFigures) {
      const own = figuresIn(judged);
      // A month is a word in English and Portuguese and a numeral in Japanese,
      // Korean, Chinese and Vietnamese, so a faithful translation of "31
      // December 2025" carries a 12 the source never wrote in digits. Asked the
      // same way the fidelity comparison asks it, so the two rules cannot
      // disagree about one date.
      if (
        own.size > 0 &&
        [...own].every(
          figure =>
            sourceFigures.has(figure) ||
            spelledOutInSource(figure, sourceValue ?? ''),
        )
      ) continue;
    }
    found.push(block.length > 120 ? `${block.slice(0, 117)}…` : block);
  }
  return found;
}

/**
 * The figures in a stretch of text, compared by value rather than by spelling.
 *
 * Separators are part of the spelling, not the number: 27.5 in English is 27,5
 * in Portuguese and German, and 1,200 is 1.200. This kept them, so a translation
 * that localised a decimal point — which is what a correct translation does —
 * looked like it had introduced a figure the source never stated. On the first
 * rebuild batch that was 146 rows.
 *
 * translationFidelity.normalizeNumber has always stripped them for exactly this
 * reason. The two halves of the rule set now agree on what makes two figures the
 * same.
 */
function figuresIn(value: string): Set<string> {
  const out = new Set<string>();
  // Separators that belong inside a figure, and no others. Admitting an ordinary
  // space made this read a run of figures as one: "Law 11.196, of 21 December
  // 2005" came back as a single 212005 and matched nothing the translation
  // wrote, so a correctly translated date was reported as a figure with no law
  // named. Kept identical to translationFidelity.NUMBER_PATTERN \u2014 the two halves
  // of the rule set have to agree on where one figure ends and the next begins.
  for (const match of (value ?? '').matchAll(/\d+(?:[.,\u00A0\u202F\u2009]\d+)*/gu)) {
    const normalized = match[0].replace(/[.,\u00A0\u202F\u2009]/gu, '');
    if (normalized) out.add(normalized);
  }
  return out;
}

/** Absolute outcomes that make a tax or legal proposition unsafe. */
const ABSOLUTE_OUTCOME_PATTERNS: readonly RegExp[] = [
  /\b(?:always|never|in all cases)\s+(?:be\s+)?(?:tax(?:ed|able)?|exempt|deductible|allow(?:ed)?|disallow(?:ed)?|prohibit(?:ed)?|liable|due|required|appl(?:y|ies|icable)|qualif(?:y|ies)|eligible|withheld|refundable)\b/iu,
  /\b(?:deadline|filing period|limitation period|tax period)\b[^.!?]{0,80}\bcannot be extended\b/iu,
  /\b(?:guarantees?|guaranteed|eliminates)\b[^.!?]{0,80}\b(?:tax|duty|levy|liability|penalty|interest|assessment|audit)\b/iu,
  /\b(?:no exceptions?|without exception)\b/iu,
];

/**
 * "Guarantee" also names a body of law — the constitutional and procedural
 * rights a taxpayer holds. That noun sense promises nothing about how a tax rule
 * applies, so it is removed before the absolute-claim patterns read a sentence.
 */
const LEGAL_GUARANTEE_NOUN_PATTERNS: readonly RegExp[] = [
  /\b(?:constitutional|fundamental|individual|legal|procedural|statutory|due[- ]process|taxpayer(?:'s|s'|s)?|human[- ]rights?)\s+guarantees?\b/giu,
  /\bguarantees?\s+(?:of|under|enshrined|provided|granted|afforded|set out|established)\b/giu,
];

/** The same sentence with the legal noun sense of "guarantee" set aside. */
function withoutLegalGuarantees(sentence: string): string {
  return LEGAL_GUARANTEE_NOUN_PATTERNS.reduce(
    (text, pattern) => text.replace(pattern, ' '),
    sentence,
  );
}

/** Sentences are the unit of meaning for an absolute legal claim. */
function proseSentences(value: string): string[] {
  return value
    .replace(/<\/(?:p|li|h[1-6])>/gi, '. ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/gu, ' ')
    .split(/(?<=[.!?…。！？؟।])\s+/u)
    .map(sentence => sentence.trim())
    .filter(Boolean);
}

/**
 * The banned absolutes this text uses, quoted from the sentence itself so the
 * editor can find the wording on the page.
 */
export function findAbsoluteClaims(value: string | null | undefined): string[] {
  const found: string[] = [];
  for (const sentence of proseSentences(value ?? '')) {
    const readable = withoutLegalGuarantees(sentence);
    for (const pattern of ABSOLUTE_OUTCOME_PATTERNS) {
      const match = pattern.exec(readable);
      if (match) found.push(match[0].trim());
    }
  }
  return found;
}

// ---------------------------------------------------------------------------
// The one input contract
// ---------------------------------------------------------------------------

/**
 * What every screen has to hand the rules.
 *
 * Until now seven call sites each assembled their own subset of fields, so one
 * language could read "2 problems" in the topic editor, appear in a collection
 * bubble and still count as nothing waiting on the bulk card. The shape the
 * editor's language dropdown used — the whole saved row plus the topic's rule —
 * is the correct one, and this builder is now the only way to produce it.
 */
export interface TopicComplianceTopic {
  id?: string;
  name: string;
  slug: string;
  translation_rule?: string | null;
}

/** One language's saved row, named as the database names it. */
export interface TopicComplianceRow {
  translated_name?: string | null;
  translated_slug?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  aio_snippet?: string | null;
  translated_body?: string | null;
  faq_items?: Array<{ question: string; answer: string }> | null;
}

/** The page text's length and questions, when the text itself is not carried. */
export interface TopicComplianceAudit {
  body_length?: number | null;
  faq_items?: Array<{ question: string; answer: string }> | null;
}

export interface TopicComplianceInputSource {
  /** The language being judged. */
  locale: string;
  /** The topic's own language, so shared fields are judged only there. */
  sourceLocale: string;
  topic: TopicComplianceTopic;
  /** The saved row for this language; null or undefined when it has none. */
  row?: TopicComplianceRow | null;
  /** The row in the topic's own language, so a floor is capped by the source. */
  sourceRow?: TopicComplianceRow | null;
  /** Page-text length and questions, when the text is not loaded. */
  audit?: TopicComplianceAudit | null;
  /** The same reading for the topic's own language. */
  sourceAudit?: TopicComplianceAudit | null;
  duplicates?: Partial<Record<TopicComplianceField, string[]>>;
  /**
   * The unsaved form, for the language open in the editor. Only the fields the
   * form owns are replaced; everything else still comes from the row.
   */
  draft?: {
    name?: string | null;
    slug?: string | null;
    metaTitle?: string | null;
    metaDescription?: string | null;
    aioSnippet?: string | null;
    body?: string | null;
    faqItems?: Array<{ question: string; answer: string }> | null;
  };
}

function faqOf(
  row: TopicComplianceRow | null | undefined,
  audit: TopicComplianceAudit | null | undefined,
): Array<{ question: string; answer: string }> | null | undefined {
  if (row && Array.isArray(row.faq_items)) return row.faq_items;
  if (audit && Array.isArray(audit.faq_items)) return audit.faq_items;
  if (row && row.faq_items === null) return null;
  if (audit && audit.faq_items === null) return null;
  return undefined;
}

/** The evidence the rules are given for one language. The only assembly there is. */
export function buildTopicComplianceInput(
  source: TopicComplianceInputSource,
): TopicComplianceInput {
  const { locale, topic, row, sourceRow, audit, sourceAudit, draft } = source;
  const isSourceLocale = neutralLocale(locale) === neutralLocale(source.sourceLocale);
  const pick = <T,>(drafted: T | undefined, saved: T): T => (drafted !== undefined ? drafted : saved);

  const bodyText = pick(draft?.body, row?.translated_body ?? undefined);
  const faqItems = pick(draft?.faqItems, faqOf(row, audit));

  return {
    locale,
    isSourceLocale,
    name: pick(draft?.name, row?.translated_name ?? (isSourceLocale ? topic.name : null)) ?? null,
    slug: pick(draft?.slug, row?.translated_slug ?? (isSourceLocale ? topic.slug : null)) ?? null,
    metaTitle: pick(draft?.metaTitle, row?.meta_title ?? null) ?? null,
    metaDescription: pick(draft?.metaDescription, row?.meta_description ?? null) ?? null,
    aioSnippet: pick(draft?.aioSnippet, row?.aio_snippet ?? null) ?? null,
    // The text when it is loaded, its length when it is not — never neither.
    body: bodyText !== undefined ? bodyText : undefined,
    bodyLength: bodyText === undefined ? audit?.body_length ?? undefined : undefined,
    faqItems,

    translationRule: topic.translation_rule ?? null,
    sourceSlug: topic.slug,
    sourceName: sourceRow?.translated_name ?? topic.name,
    sourceMetaTitle: sourceRow?.meta_title ?? null,
    sourceMetaDescription: sourceRow?.meta_description ?? null,
    sourceAioSnippet: sourceRow?.aio_snippet ?? null,
    sourceBody: sourceRow?.translated_body ?? null,
    sourceBodyForLength: sourceRow?.translated_body ?? null,
    sourceBodyLength: sourceAudit?.body_length ?? undefined,
    duplicates: source.duplicates,
  };
}

/** Every rule this language breaks, in field order. */
export function checkTopicLocale(input: TopicComplianceInput): TopicComplianceIssue[] {
  const issues: TopicComplianceIssue[] = [];
  const { locale } = input;

  checkField('name', input.name, locale, issues, input.sourceName);
  checkField('slug', input.slug, locale, issues);
  checkField('meta_title', input.metaTitle, locale, issues, input.sourceMetaTitle);
  checkField('meta_description', input.metaDescription, locale, issues, input.sourceMetaDescription);
  checkField('aio_snippet', input.aioSnippet, locale, issues, input.sourceAioSnippet);
  // The body is only judged where it is loaded: `undefined` means "not
  // read here" (the topics table never fetches the long text), while `null` or
  // an empty string means the language really has no body.
  if (input.body !== undefined) {
    checkField('body', input.body, locale, issues, input.sourceBody ?? input.sourceBodyForLength);
  } else if (input.bodyLength !== undefined) {
    // Length only: the reports know how long the page text is without carrying it.
    checkFieldLength(
      'body',
      input.bodyLength,
      locale,
      issues,
      input.sourceBodyForLength,
      input.sourceBodyLength,
    );
  }
  // Questions and answers, when they are loaded here.
  if (input.faqItems !== undefined) checkFaqField(input.faqItems, locale, issues);


  // Written in the right language? Wording this topic keeps on purpose — its
  // official name and its original address, when the rule says to keep them —
  // never counts against the language it appears in.
  const keptWording =
    input.translationRule === 'keep_name'
      ? [input.sourceName ?? '', input.sourceSlug ?? ''].filter(Boolean)
      : [];
  const languageChecked: Array<[TopicComplianceField, string | null | undefined]> = [
    ['meta_title', input.metaTitle],
    ['meta_description', input.metaDescription],
    ['aio_snippet', input.aioSnippet],
    ['body', input.body],
  ];
  for (const [field, value] of languageChecked) {
    if (!value) continue;
    if (looksLikeLocale(value, locale, { keep: keptWording }) === false) {
      issues.push({
        field,
        locale,
        code: 'wrong_language',
        message: `${TOPIC_COMPLIANCE_FIELD_LABELS[field]} does not look like ${locale} — it reads as English.`,
      });
    }
  }

  // A word written in another language's writing system. The text can be the
  // right length and read as the right language and still carry an untranslated
  // word from a third script — that is what this catches.
  const scriptChecked: Array<[TopicComplianceField, string | null | undefined]> = [
    ['name', input.name],
    ['meta_description', input.metaDescription],
    ['aio_snippet', input.aioSnippet],
    ['body', input.body],
  ];
  for (const [field, value] of scriptChecked) {
    if (!value) continue;
    const foreign = findForeignScript(value, locale, keptWording);
    if (!foreign) continue;
    issues.push({
      field,
      locale,
      code: 'foreign_script',
      message: `${TOPIC_COMPLIANCE_FIELD_LABELS[field]} contains a word in ${foreign.script}, not in this language: “${foreign.word}”.`,
    });
  }
  if (input.faqItems) {
    for (const item of input.faqItems) {
      const foreign =
        findForeignScript(item.question, locale, keptWording) ??
        findForeignScript(item.answer, locale, keptWording);
      if (!foreign) continue;
      issues.push({
        field: 'faq_items',
        locale,
        code: 'foreign_script',
        message: `${TOPIC_COMPLIANCE_FIELD_LABELS.faq_items}: a word is written in ${foreign.script}, not in this language: “${foreign.word}”.`,
      });
      break;
    }
  }

  // The writer answered with the request instead of the text.
  const echoChecked: Array<[TopicComplianceField, string | null | undefined]> = [
    ['name', input.name],
    ['meta_title', input.metaTitle],
    ['meta_description', input.metaDescription],
    ['aio_snippet', input.aioSnippet],
    ['body', input.body],
  ];
  for (const [field, value] of echoChecked) {
    if (!value) continue;
    if (isEchoedInstruction(value)) {
      issues.push({
        field,
        locale,
        code: 'echoed_prompt',
        message: `${TOPIC_COMPLIANCE_FIELD_LABELS[field]} repeats the instruction given to the writer instead of the text — write it again.`,
      });
    }
  }

  // A translation that changed a figure, dropped a link or lost an acronym is
  // not a translation. Only judged where the source wording is supplied.
  if (!input.isSourceLocale) {
    const compared: Array<[TopicComplianceField, string | null | undefined, string | null | undefined]> = [
      ['meta_title', input.sourceMetaTitle, input.metaTitle],
      ['meta_description', input.sourceMetaDescription, input.metaDescription],
      ['aio_snippet', input.sourceAioSnippet, input.aioSnippet],
      ['body', input.sourceBody, input.body],
    ];
    for (const [field, sourceValue, value] of compared) {
      if (!sourceValue || !value) continue;
      const gap = fidelityGaps(sourceValue, value);
      if (isFaithful(gap)) continue;
      issues.push({
        field,
        locale,
        code: 'unfaithful',
        message: `${TOPIC_COMPLIANCE_FIELD_LABELS[field]} does not match the original — ${fidelityMessage(gap)}`,
      });
    }
  }

  // The name is a code word or has no letters at all — not a term.
  if (isJunkName(input.name)) {
    issues.push({
      field: 'name',
      locale,
      code: 'malformed',
      message: 'Name is not a term — write the topic name in this language.',
    });
  } else if (isProseName(input.name)) {
    issues.push({
      field: 'name',
      locale,
      code: 'malformed',
      message: 'Name is a sentence, not a term — write the topic name only.',
    });
  }


  // The same text on another topic is copied content, whichever one is right.
  for (const [field, names] of Object.entries(input.duplicates ?? {})) {
    if (!names || names.length === 0) continue;
    issues.push({
      field: field as TopicComplianceField,
      locale,
      code: 'duplicate',
      message: `${TOPIC_COMPLIANCE_FIELD_LABELS[field as TopicComplianceField]} is the same as: ${names.join(', ')}.`,
    });
  }

  // A prompt rule is a suggestion; this is the enforcement. The long text may
  // quantify, but only where it names what sets the figure, and no text of any
  // length may claim a tax rule holds without exception.
  for (const [field, value, sourceValue] of [
    ['body', input.body, input.sourceBody],
    ['aio_snippet', input.aioSnippet, input.sourceAioSnippet],
  ] as Array<[TopicComplianceField, string | null | undefined, string | null | undefined]>) {
    if (!value) continue;
    const unsourced = findUnsourcedFigures(
      value,
      input.isSourceLocale ? undefined : sourceValue ?? undefined,
    );
    if (unsourced.length > 0) {
      issues.push({
        field,
        locale,
        code: 'unsourced_figure',
        message:
          `${TOPIC_COMPLIANCE_FIELD_LABELS[field]} states a figure without naming what sets it: ` +
          `“${unsourced[0]}”. Name the law, article or regulation, or remove the figure.`,
      });
    }
  }
  for (const [field, value] of [
    ['body', input.body],
    ['aio_snippet', input.aioSnippet],
  ] as Array<[TopicComplianceField, string | null | undefined]>) {
    if (!value) continue;
    const absolutes = findAbsoluteClaims(value);
    if (absolutes.length > 0) {
      issues.push({
        field,
        locale,
        code: 'absolute_claim',
        message:
          `${TOPIC_COMPLIANCE_FIELD_LABELS[field]} claims a tax rule holds without exception ` +
          `(“${absolutes[0]}”). Write “generally”, “typically” or “in most cases” instead.`,
      });
    }
  }



  // The page title carries the brand of the site that serves this language.
  if (!hasCorrectBrandSuffix(input.metaTitle, locale)) {
    issues.push({
      field: 'meta_title',
      locale,
      code: 'brand_suffix',
      message: `Page title has to end with “| ${brandSuffixForLocale(locale)}”.`,
    });
  }

  // The page title is the topic's name plus the site name, nothing else: a
  // title carrying other wording was written instead of built.
  const expectedTitle = brandedTitleFromName(input.name, locale);
  if (expectedTitle && (input.metaTitle ?? '').trim() && input.metaTitle?.trim() !== expectedTitle) {
    issues.push({
      field: 'meta_title',
      locale,
      code: 'malformed',
      message: `Page title has to read “${expectedTitle}”.`,
    });
  }


  // The name has to obey the topic's own rule.
  //
  // keep_name marks a term that does not translate: a statutory instrument or a
  // proper name — ICMS, Pillar Two, Drawback Aduaneiro — which every language
  // refers to as it is. Rendering it in the local language invents a term that
  // nobody searches for and that no law calls it.
  //
  // Nothing checked this. 276 of 1,330 keep_name rows had been translated
  // anyway: 245 lost the name entirely ("Cesta Básica Nacional" stored as
  // "Panier de biens essentiels national") and 31 kept it inside a translation
  // ("CRS" as "المعيار المشترك لإعداد التقارير (CRS)"). The rule existed only to
  // switch other checks off.
  if (!input.isSourceLocale && input.translationRule === 'keep_name' && input.sourceName) {
    const written = (input.name ?? '').trim();
    if (written && written !== input.sourceName.trim()) {
      issues.push({
        field: 'name',
        locale,
        code: 'name_rule',
        message: `This topic keeps its original name in every language: ${input.sourceName}.`,
      });
    }
  }

  // The address has to obey the topic's own rule.
  if (!input.isSourceLocale && input.translationRule === 'keep_name' && input.sourceSlug) {
    if ((input.slug ?? '').trim() && input.slug !== input.sourceSlug) {
      issues.push({
        field: 'slug',
        locale,
        code: 'address_rule',
        message: `This topic keeps its original address in every language: ${input.sourceSlug}.`,
      });
    }
  }

  // A translate-topic whose address still reads as the original language has an
  // address nobody in this language would search for — unless that address is
  // an acronym or official form, which every language uses as it is.
  // The address is built from the translated name, so if the name was translated
  // and the address still matches the source's, ASCII-stripping produced the same
  // string — "Monofasia Tributária" and the Spanish "Monofasia Tributaria" both
  // reduce to `monofasia-tributaria`. That is the correct address for the
  // translated name, not the source's address left in place, and reporting it
  // asks for an address that cannot exist. Three rows on 2026-09-21.
  const nameWasTranslated =
    (input.sourceName ?? '').trim() !== '' &&
    (input.name ?? '').trim() !== '' &&
    normalize(input.name) !== normalize(input.sourceName);

  if (
    !input.isSourceLocale &&
    input.translationRule === 'translate' &&
    (input.sourceSlug ?? '').trim() &&
    (input.slug ?? '').trim() &&
    !isAcronymSlug(input.sourceSlug) &&
    !isAcceptedBorrowedTerm(input.slug, locale) &&
    !nameWasTranslated &&
    normalize(input.slug) === normalize(input.sourceSlug)
  ) {
    issues.push({
      field: 'slug',
      locale,
      code: 'address_not_translated',
      message: 'Web address is still the original one — this topic translates its address.',
    });
  }

  // A translated language whose name is still the original wording was never
  // translated at all — and when that language is English, every other
  // language is translated from it, so the original wording spreads.
  if (
    !input.isSourceLocale &&
    input.translationRule !== 'keep_name' &&
    (input.sourceName ?? '').trim() &&
    (input.name ?? '').trim() &&
    !isAcceptedBorrowedTerm(input.name, locale) &&
    normalize(input.name) === normalize(input.sourceName)
  ) {
    issues.push({
      field: 'name',
      locale,
      code: 'name_not_translated',
      message: 'Name is still the original wording — it was never translated.',
    });
  }

  // The topic's own language, judged on its own name: a topic filed as one
  // language whose name is written in another was labelled wrongly at creation,
  // and nothing downstream can repair it — every other language is translated
  // from that name. Reported so it shows as work to do instead of passing
  // silently just because it is the original.
  if (input.isSourceLocale && sourceNameLanguageMismatch(input.name, locale, input.translationRule)) {
    issues.push({
      field: 'name',
      locale,
      code: 'wrong_language',
      message: `Name is not written in ${locale} — either write it in this language or change the topic's original language.`,
    });
  }


  // A title or description that stops mid-sentence ("… Tax in | Tax.Network")
  // was cut before it said anything — it reads as broken to a reader.
  const truncationChecked: Array<[TopicComplianceField, string | null | undefined]> = [
    ['meta_title', input.metaTitle],
    ['meta_description', input.metaDescription],
  ];
  for (const [field, value] of truncationChecked) {
    if (!value) continue;
    if (endsUnfinished(value, locale)) {
      issues.push({
        field,
        locale,
        code: 'meta_truncated',
        message: `${TOPIC_COMPLIANCE_FIELD_LABELS[field]} stops mid-sentence — finish the wording.`,
      });
    }
  }

  return issues;
}

/**
 * Words a sentence never ends on, in the Latin-script languages we serve. A
 * page title ending here was cut before its last word, which is what
 * "Understand X Tax in | Tax.Network" is.
 */
const DANGLING_FINAL_WORDS = new Set([
  'in', 'on', 'of', 'for', 'to', 'and', 'or', 'the', 'a', 'an', 'with', 'by', 'at', 'from', 'as',
  'de', 'da', 'do', 'dos', 'das', 'em', 'para', 'e', 'com', 'y', 'en', 'del', 'la', 'le', 'les',
  'des', 'du', 'und', 'der', 'die', 'das', 'von', 'di', 'per', 'con',
  // A sentence that stops where the citation should be. These are the words a
  // legal sentence ends on only when it was cut off before the number:
  // "Consagrado no Art.", "conforme Art.", "regulado pela Lei".
  'art', 'arts', 'artigo', 'artigos', 'articulo', 'article', 'lei', 'leis', 'ley', 'loi',
  'inciso', 'incisos', 'paragrafo', 'parag', 'alinea', 'decreto', 'section', 'sec',
  'law', 'act', 'n', 'no', 'nr', 'num', 'item', 'clause', 'chapter', 'cap',
]);

/**
 * The last complete Unicode word, so accents cannot create false boundaries.
 *
 * A trailing full stop is stripped first. Without that, a sentence ending on an
 * abbreviated citation — "Consagrado no Art." — matched nothing at all, because
 * the pattern is anchored at the end and the string ends in a period. The check
 * then read it as a finished sentence and let it through. Two source fields in
 * the corpus end exactly that way, and between them they made 60 translations
 * fail the fidelity comparison for correctly finishing the citation.
 *
 * Only a full stop is stripped. A comma or colon at the end is already caught by
 * DANGLING_PUNCTUATION and must stay visible to it.
 */
function finalWord(value: string): string {
  const withoutFullStop = value.replace(/\.+$/u, '');
  return withoutFullStop.match(/[\p{L}\p{M}]+$/u)?.[0]?.toLocaleLowerCase() ?? '';
}

/**
 * Punctuation that leaves a sentence hanging in any script: Latin commas and
 * dashes, the CJK and Arabic commas, and the Devanagari and Arabic separators.
 * A language whose sentences simply end without a Latin full stop is never
 * called unfinished for that reason alone.
 */
const DANGLING_PUNCTUATION = /[,;:\-–—、，；：؛٫,]$/;

/** Abbreviations that, followed by a full stop, still expect a citation number. */
const CITATION_ABBREVIATIONS: ReadonlySet<string> = new Set([
  'art', 'arts', 'parag', 'sec', 'n', 'no', 'nr', 'num', 'cap',
]);

/** Does this text stop mid-sentence, ignoring the brand suffix at the end? */
export function endsUnfinished(value: string | null | undefined, locale: string): boolean {
  const suffix = `| ${brandSuffixForLocale(locale)}`;
  let text = (value ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (text.endsWith(suffix)) text = text.slice(0, -suffix.length).trim();
  text = text.replace(/[|·—–-]+$/, '').trim();
  if (!text) return false;
  if (DANGLING_PUNCTUATION.test(text)) return true;
  // The dangling-word list only exists for Latin-script languages: applying it
  // to another script would flag correct text for lacking English function
  // words. Languages written in their own script are judged on punctuation only.
  if (SCRIPT_BY_LOCALE[neutralLocale(locale)]) return false;
  const word = finalWord(text);
  // A full stop after a whole word ("…the CARES Act.") ends a sentence; only an
  // abbreviation before the stop ("conforme Art.") means the citation was cut.
  if (/\.$/u.test(text) && !CITATION_ABBREVIATIONS.has(word)) return false;
  return DANGLING_FINAL_WORDS.has(word);
}

/**
 * An address that is an acronym or an official short form ("irpj", "cnpj",
 * "gilti"): every language keeps it, so it is never "not translated".
 */
export function isAcronymSlug(slug: string | null | undefined): boolean {
  const value = (slug ?? '').trim().toLowerCase();
  if (!value) return false;
  return /^[a-z0-9]{2,8}$/.test(value);
}

/**
 * Said next to a language that keeps the original name or address because the
 * topic's own address rule says so — not a problem, a setting.
 */
export function keptByRuleNote(
  isSourceLocale: boolean,
  translationRule: string | null | undefined,
): string | null {
  if (isSourceLocale || translationRule !== 'keep_name') return null;
  return 'This topic keeps its original name and web address in every language (address rule).';
}

/** Only the issues that belong to one field, for rendering under the input. */
export function issuesForField(
  issues: readonly TopicComplianceIssue[],
  field: TopicComplianceField,
): TopicComplianceIssue[] {
  return issues.filter(issue => issue.field === field);
}

export function issueMessages(
  issues: readonly TopicComplianceIssue[],
  field: TopicComplianceField,
): string[] {
  return issuesForField(issues, field).map(issue => issue.message);
}

// ---------------------------------------------------------------------------
// Cross-topic duplicate detection
// ---------------------------------------------------------------------------

export interface TopicTextRow {
  topicId: string;
  locale: string;
  name: string | null;
  slug: string | null;
  metaTitle: string | null;
}

function normalize(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * Which other topics already use this topic's name, address or page title in
 * the same language. Names are returned, so the message can be read.
 */
export function findDuplicates(
  rows: readonly TopicTextRow[],
  namesById: ReadonlyMap<string, string>,
  topicId: string,
  locale: string,
): Partial<Record<TopicComplianceField, string[]>> {
  const mine = rows.find(row => row.topicId === topicId && row.locale === locale);
  if (!mine) return {};
  const result: Partial<Record<TopicComplianceField, string[]>> = {};
  const pairs: Array<[TopicComplianceField, string]> = [
    ['name', normalize(mine.name)],
    ['slug', normalize(mine.slug)],
    ['meta_title', normalize(mine.metaTitle)],
  ];

  for (const [field, value] of pairs) {
    if (!value) continue;
    const clash = rows
      .filter(row => row.locale === locale && row.topicId !== topicId)
      .filter(row => {
        const other =
          field === 'name' ? row.name : field === 'slug' ? row.slug : row.metaTitle;
        return normalize(other) === value;
      })
      .map(row => namesById.get(row.topicId) ?? row.topicId);
    if (clash.length > 0) result[field] = Array.from(new Set(clash));
  }

  return result;
}

// ---------------------------------------------------------------------------
// Languages already written with text that cannot stand
// ---------------------------------------------------------------------------

/** Words a name can never be: markup, code or an empty value written out. */
const JUNK_NAMES = new Set([
  'html', 'body', 'div', 'span', 'p', 'br', 'text', 'title', 'name', 'json', 'xml',
  'null', 'undefined', 'none', 'n/a', 'na', 'nan', 'string', 'translation', 'translate',
  'topic', 'error', 'todo', 'test',
]);

/**
 * A name that cannot be a term: a bare markup or code word, or nothing but
 * punctuation and digits. Those rows look written but have to be redone.
 */
export function isJunkName(value: string | null | undefined): boolean {
  const text = (value ?? '').trim();
  if (!text) return false;
  const bare = text.toLowerCase().replace(/^[<[({"']+|[>\])}"':.]+$/g, '').trim();
  if (JUNK_NAMES.has(bare)) return true;
  // No letter at all in any writing system: punctuation or numbers only.
  return !/\p{L}/u.test(text);
}

/** A minute of slack, so a run that wrote a language alongside the original is not flagged. */
const OUTDATED_GRACE_MS = 60_000;

/**
 * True when a language no longer matches the original it was translated from.
 *
 * The fingerprint of the source text decides it whenever both sides carry one:
 * saving the original again without changing its text leaves every language
 * current. The timestamp comparison is only the fallback for rows written before
 * fingerprints existed.
 */
export function localeRowIsOutdated(
  updatedAt: string | null | undefined,
  sourceUpdatedAt: string | null | undefined,
  fingerprints?: {
    /** The fingerprint this language was translated from. */
    rowSourceHash?: string | null;
    /** The fingerprint of the original as it stands now. */
    currentSourceHash?: string | null;
  },
): boolean {
  const rowHash = (fingerprints?.rowSourceHash ?? '').trim();
  const currentHash = (fingerprints?.currentSourceHash ?? '').trim();
  if (rowHash && currentHash) return rowHash !== currentHash;

  if (!updatedAt || !sourceUpdatedAt) return false;
  const written = Date.parse(updatedAt);
  const source = Date.parse(sourceUpdatedAt);
  if (Number.isNaN(written) || Number.isNaN(source)) return false;
  return written < source - OUTDATED_GRACE_MS;
}

/**
 * A language that looks written but cannot be published: a field carrying the
 * request instead of the text, a name that is not a term at all, or a body
 * far below the thin-page floor. Those languages have to be redone even by a run
 * that only fills what is missing — otherwise the bad text stays forever because
 * the row is not empty.
 */
export function topicRowNeedsRedo(row: {
  name?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  aioSnippet?: string | null;
  body?: string | null;
  /** The language the row belongs to, so its own thin-page floor is used. */
  locale?: string | null;
  /** When this language was last written. */
  updatedAt?: string | null;
  /** When the topic's own language was last written. */
  sourceUpdatedAt?: string | null;
} | null | undefined): boolean {
  if (!row) return false;
  const echoed = [row.name, row.metaTitle, row.metaDescription, row.aioSnippet, row.body]
    .some(value => isEchoedInstruction(value));
  if (echoed) return true;
  if (isJunkName(row.name) || isProseName(row.name)) return true;
  if (localeRowIsOutdated(row.updatedAt, row.sourceUpdatedAt)) return true;
  const bodyLength = visibleTextLength(row.body);
  return bodyLength > 0 && bodyLength < limitForLocale(row.locale ?? 'en', 'body').min;
}

